<template>
  <card body-classes="px-0 pb-1" class="no-border-card" footer-classes="pb-2">
    <template slot="header">
      <h3 class="mb-0 d-inline-block">{{ title }}</h3>
    </template>

    <div>
      <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
        <el-select v-model="pagination.perPage" class="select-primary pagination-select" placeholder="Per page">
          <el-option
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
            class="select-primary"
          >
          </el-option>
        </el-select>

        <base-input class="my-0 mx-2">
          <el-autocomplete
            v-model="sellerSelected"
            cy-id="seller-select"
            placeholder="Seleccione Asesor Técnico"
            :fetch-suggestions="sellerOptions"
            @select="onSellerSelected"
          />
        </base-input>

        <base-input class="my-0 mx-2">
          <el-autocomplete
            v-model="distributorSelected"
            cy-id="distributor-select"
            placeholder="Seleccione Distribuidor"
            :fetch-suggestions="distributorOptions"
            @select="onDistributorSelected"
          />
        </base-input>

        <base-input v-if="showStateFilter && states.length > 0" class="mt-1">
          <el-select v-model="mixStateFilter" class="select-primary" placeholder="Estado" value-key="value">
            <el-option :value="null" class="select-primary" label="Todos"> </el-option>
            <el-option
              v-for="item in states"
              :key="item.label"
              :label="item.label"
              :value="item.id"
              class="select-primary"
            >
            </el-option>
          </el-select>
        </base-input>

        <base-input v-if="!filterCampaign && campaigns.length > 0">
          <el-select v-model="campaignSelected" class="select-primary" placeholder="Campaña" value-key="value">
            <el-option :value="null" class="select-primary" label="Todas las campañas"></el-option>
            <el-option
              v-for="item in campaigns"
              :key="item.id"
              :label="item.name"
              :value="item.id"
              class="select-primary"
            >
            </el-option>
          </el-select>
        </base-input>

        <div>
          <base-input v-model="mixSearchQuery" placeholder="Buscar..." prepend-icon="fas fa-search"></base-input>
        </div>
      </div>
    </div>

    <div class="col-12">
      <active-filters
        v-if="hasActiveChartFilters"
        :disable-reset="true"
        :crop="chartCrop"
        :variety="chartVariety"
        :month="chartMonth"
        :distributor="chartDistributor"
        @reset="refresh"
        @resetDistributor="refreshDistributor"
      />
    </div>

    <div class="table-responsive">
      <el-table :data="queriedData" header-row-class-name="thead-light" row-key="id" @sort-change="sortChange">
        <el-table-column
          label="Productor"
          min-width="205px"
          prop="producer"
          default-sort="ascending"
          sortable
          :sort-orders="['ascending', 'descending', null]"
        >
          <template #default="{ row }">
            <router-link
              v-if="$can('show', 'producers')"
              :to="{ name: 'ProducerShow', params: { id: row.producer.id } }"
            >
              {{ row.producer.businessName }}
            </router-link>
            <span v-else>{{ row.producer.businessName }}</span>

            <div v-if="row.distributorName && row.sellerName">
              <small>{{ row.distributorName }}</small
              ><br />
              <small>{{ row.sellerName }}</small>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Estado" min-width="100px" prop="comment">
          <template #default="{ row }">
            <badge v-if="row.deletedAt" class="mb-0" type="danger"> Eliminada</badge>
            <badge v-else-if="row.state" class="mb-0" :type="row.state.color">
              {{ row.state.label }}
            </badge>
            <badge v-else class="mb-0" type="primary"> Sin Seguimiento</badge>
          </template>
        </el-table-column>

        <el-table-column align="right" label="" min-width="70px">
          <div slot-scope="{ row }" class="d-flex flex-column">
            <div v-if="!row.comment">
              <router-link
                v-if="$can('show', 'projections')"
                :to="{ name: 'ProjectionShow', params: { id: row.id } }"
                class="btn base-button btn-outline-info btn-sm"
              >
                <i class="fas fa-eye"></i>
              </router-link>
            </div>
            <div v-if="!row.comment" class="mt-1">
              <button
                v-if="$can('destroy', 'projections')"
                title="Eliminar"
                class="btn base-button btn-outline-danger btn-sm"
                @click="deleteProjection(row.id)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </el-table-column>

        <el-table-column label="Cotización" min-width="125px" prop="lastQuotationDate" sortable>
          <template #default="{ row }">
            <span v-if="row.lastQuotationDate"> {{ row.lastQuotationDate | date('DD/MM/YYYY') }}</span>
            <span v-else> Sin Cotización </span>
          </template>
        </el-table-column>

        <el-table-column label="Fecha Seguimiento" min-width="110px" prop="lastActivityDate" sortable>
          <template #default="{ row }">
            <span v-if="row.lastActivityDate">{{ row.lastActivityDate | date('DD/MM/YYYY') }}</span>
            <span v-else> Sin Seguimiento </span>
          </template>
        </el-table-column>

        <el-table-column label="Proximo Seguimiento" min-width="110px" prop="nextFollowUpDate" sortable>
          <template #default="{ row }">
            <span v-if="row.nextFollowUpDate"> {{ row.nextFollowUpDate | date('DD/MM/YYYY') }}</span>
            <span v-else> - </span>
          </template>
        </el-table-column>

        <el-table-column label="Venta Anterior" min-width="85px" prop="previousQuantity">
          <template #default="{ row }">
            <span v-if="row.previousQuantity">Cant: {{ row.previousQuantity }}</span>
            <span v-else> - </span><br />
            <span v-if="row.previousPeriod">Mes: {{ row.previousPeriod }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Variedad" min-width="170px" prop="variety.name">
          <template #default="{ row }">
            <div>{{ row.variety.name }}</div>
            <small>{{ row.variety.crop.name }}</small>
            <div class="font-weight-bold">{{ row.campaign.name }}</div>
          </template>
        </el-table-column>

        <el-table-column label="Cant." min-width="60px" prop="quantity">
          <template #default="{ row }">
            <span>{{ row.quantity }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Diferencia" min-width="60px" prop="difference">
          <template #default="{ row }">
            <span v-if="row.previousQuantity">{{ row.quantity - row.previousQuantity }}</span>
            <span v-else> - </span>
          </template>
        </el-table-column>

        <el-table-column label="Cant. a Sembrar" min-width="73px" prop="competitorQuantity">
          <template #default="{ row }">
            <span>{{ row.competitorQuantity || '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Variedad comp." min-width="80px" prop="competitorVariety">
          <template #default="{ row }">
            <span>{{ row.competitorVariety || '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Inc." min-width="60px" prop="quantity">
          <template #default="{ row }">
            <span v-if="row.competitorQuantity">
              {{ ((row.quantity * 100) / row.competitorQuantity).toFixed(0) }}%
            </span>
            <span v-else>0</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Mes de Compra"
          min-width="75px"
          prop="period"
          sortable
          default-sort="ascending"
          :sort-orders="['ascending', 'descending', null]"
        >
          <template #default="{ row }">
            <span>{{ row.period }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
      <div>
        <p class="card-category">
          Mostrando {{ from + 1 }} a {{ to }} de un total de {{ pagination.total }} proyecciones
        </p>
      </div>
      <base-pagination
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :show-first-page-button="true"
        :show-last-page-button="true"
        :total="pagination.total"
        class="pagination-no-border"
        @input="changePage"
      >
      </base-pagination>
    </div>
  </card>
</template>

<script>
import { Table, TableColumn, DropdownMenu, DropdownItem, Dropdown, Select, Option, Autocomplete } from 'element-ui';
import swal from 'sweetalert2';
import { mapState } from 'vuex';

import { BasePagination } from '@/components';
import ActiveFilters from '@/components/Charts/ActiveFilters.vue';
import PaginationOnServerMixin from '@/mixins/paginationOnServerMixin';
import ProjectionStatesMixin from '@/mixins/projectionStatesMixin';
import ChartMixin from '@/mixins/chartMixin';

export default {
  name: 'ProjectionsTable',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Autocomplete.name]: Autocomplete,
    BasePagination,
    ActiveFilters,
  },

  mixins: [PaginationOnServerMixin, ProjectionStatesMixin, ChartMixin],
  props: {
    title: {
      type: String,
      default: 'Todas las proyecciones',
    },

    producer: {
      type: Number,
      default: null,
    },

    distributor: {
      type: Number,
      default: null,
    },

    variety: {
      type: Number,
      default: null,
    },

    filterCampaign: {
      type: Number,
      default: null,
    },

    showStateFilter: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      campaignSelected: null,
      tableData: [],
      modelName: 'projection',
      sellerSelected: null,
      distributorSelected: null,
    };
  },

  computed: {
    ...mapState('models/campaign', ['campaigns']),
  },

  watch: {
    filterCampaign: {
      immediate: true,
      handler() {
        this.fetchTableData();
      },
    },
  },

  mounted() {
    this.fetchDistributors();
    this.fetchSellers();
  },

  beforeDestroy() {
    this.resetChartFilters(false);
  },

  methods: {
    fetchTableData({ params } = {}) {
      this.pagination.perPage = this.perPage || 50;
      this.pagination.currentPage = this.page || this.pagination.currentPage;

      const parameters = {
        include: ['producer', 'variety.crop', 'campaign'],
        append: ['sellerName', 'distributorName'],
        'filter[distributor]': this.chartDistributor ? this.chartDistributor.id : this.distributor,
        'filter[variety]': this.chartVariety ? this.chartVariety : this.variety,
        'filter[producer]': this.producer,
        'filter[campaign]': this.campaignSelected || this.filterCampaign,
        sort: this.sort,
        perPage: this.pagination.perPage,
        page: this.pagination.currentPage,
        'filter[search]': this.mixSearchQuery,
        'filter[state]': this.mixStateFilter,
        'filter[crop]': this.chartCrop,
        'filter[period]': this.chartMonth,
        ...params,
      };

      this.$services
        .resource('projections')
        .get(parameters)
        .then((res) => {
          this.setMeta(res.data.meta);
          return res.data.data;
        })
        .then((data) => (this.tableData = data.projections));
    },

    fetchSellers() {
      const params = {
        sort: 'name',
      };

      this.$services
        .resource('sellers')
        .get(params)
        .then((res) => res.data.data)
        .then((data) => {
          this.sellers = data.sellers.map((seller) => ({ id: seller.id, value: seller.name }));
        });
    },

    fetchDistributors() {
      const params = {
        sort: 'name',
        for_select: true,
      };

      this.$services
        .resource('distributors')
        .get(params)
        .then((res) => res.data.data)
        .then((data) => {
          this.distributors = data.distributors.map((distributor) => ({
            id: distributor.id,
            value: distributor.name,
          }));
        });
    },

    sellerOptions(query, cb) {
      const options = [{ value: 'Todos', id: null }].concat(this.sellers);

      cb(
        options.filter((option) => {
          return option.value.toLowerCase().indexOf(query ? query.toLowerCase() : '') >= 0;
        })
      );
    },

    distributorOptions(query, cb) {
      const options = [{ value: 'Todos', id: null }].concat(this.distributors);

      cb(
        options.filter((option) => {
          return option.value.toLowerCase().indexOf(query ? query.toLowerCase() : '') >= 0;
        })
      );
    },

    onSellerSelected(seller) {
      if (seller) {
        const params = {
          'filter[seller_id]': seller.id,
        };
        this.fetchTableData({ params });
      }
    },

    onDistributorSelected(distributor) {
      if (distributor) {
        const params = {
          'filter[distributor_id]': distributor.id,
        };
        this.fetchTableData({ params });
      }
    },

    deleteProjection(id) {
      swal({
        title: 'Ingrese el motivo por el cual desea eliminar la proyección',
        type: 'warning',
        input: 'text',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        showLoaderOnConfirm: true,
        preConfirm: (comment) => {
          return this.$services
            .resource('projections')
            .delete(id, { comment: comment })
            .then(() => {
              const i = this.tableData.findIndex((e) => e.id === id);
              this.tableData[i].comment = comment;
            })
            .then(() => swal('Listo!', 'La proyección se ha eliminado', 'success'))
            .catch((error) => {
              swal.showValidationMessage(`Ha ocurrido un error: ${error}`);
            });
        },
        allowOutsideClick: () => !swal.isLoading(),
      });
    },

    refresh() {
      this.resetChartFilters();
      this.fetchTableData();
    },

    refreshDistributor() {
      this.setChartDistributor(null);
      this.fetchTableData();
    },
  },
};
</script>

<style scoped>
::v-deep .el-table tr td {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

::v-deep .el-table .el-table__header-wrapper thead th {
  padding: 0.5rem 0.75rem;
}
::v-deep .el-input__inner {
  height: 43px;
}
::v-deep .el-input--suffix {
  margin-top: -3px;
}
</style>
